import React, { useState, useEffect } from "react";
import Papa from "papaparse";
import Map from "./components/Map";
import Sidebar from "./components/Sidebar";
import Header from "./components/Header";
import Legend from "./components/Legend";
import ReportModal from "./components/ReportModal";
import "./styles/App.css";

function App() {
  const [facilities, setFacilities] = useState([]);
  const [filteredFacilities, setFilteredFacilities] = useState([]);
  const [groupedFacilities, setGroupedFacilities] = useState([]);
  const [filters, setFilters] = useState({
    carcinogen: "all",
    industry: "all",
    county: "all",
    searchTerm: "",
  });
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    carcinogen: 0,
    nonCarcinogen: 0,
  });
  const [shouldResetView, setShouldResetView] = useState(false);

  useEffect(() => {
    fetch("/2024_mi_filtered.csv")
      .then((response) => response.text())
      .then((csvText) => {
        Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            console.log("All columns:", Object.keys(results.data[0]));

            const parseNumber = (value) => {
              if (!value || value === "") return 0;
              const cleaned = String(value).replace(/,/g, "");
              const parsed = parseFloat(cleaned);
              return isNaN(parsed) ? 0 : parsed;
            };

            const data = results.data
              .map((row) => {
                const airReleases =
                  parseNumber(row["51. 5.1 - FUGITIVE AIR"]) +
                  parseNumber(row["52. 5.2 - STACK AIR"]);
                const waterReleases = parseNumber(row["53. 5.3 - WATER"]);
                const landReleases =
                  parseNumber(row["54. 5.4 - UNDERGROUND"]) +
                  parseNumber(row["57. 5.5.1 - LANDFILLS"]) +
                  parseNumber(row["60. 5.5.2 - LAND TREATMENT"]) +
                  parseNumber(row["62. 5.5.3A - RCRA SURFACE IM"]) +
                  parseNumber(row["64. 5.5.4 - OTHER DISPOSAL"]);

                return {
                  facilityName: row["4. FACILITY NAME"],
                  streetAddress: row["5. STREET ADDRESS"],
                  city: row["6. CITY"],
                  county: row["7. COUNTY"],
                  state: row["8. ST"],
                  zip: row["9. ZIP"],
                  latitude: parseFloat(row["12. LATITUDE"]),
                  longitude: parseFloat(row["13. LONGITUDE"]),
                  parentCompany: row["17. STANDARD PARENT CO NAME"],
                  industrySector: row["23. INDUSTRY SECTOR"],
                  chemical: row["37. CHEMICAL"],
                  carcinogen: row["46. CARCINOGEN"],
                  airReleases,
                  waterReleases,
                  landReleases,
                  totalReleases: airReleases + waterReleases + landReleases,
                };
              })
              .filter((f) => f.latitude && f.longitude);

            const grouped = Object.values(
              data.reduce((acc, row) => {
                const key = `${row.facilityName}-${row.latitude}-${row.longitude}`;
                if (!acc[key]) {
                  acc[key] = { ...row, reports: [row] };
                } else {
                  acc[key].reports.push(row);
                  acc[key].airReleases += row.airReleases;
                  acc[key].waterReleases += row.waterReleases;
                  acc[key].landReleases += row.landReleases;
                  acc[key].totalReleases += row.totalReleases;
                }
                return acc;
              }, {})
            );

            setFacilities(data);
            setFilteredFacilities(data);
            setGroupedFacilities(grouped);

            setStats({
              total: data.length,
              carcinogen: data.filter((f) => f.carcinogen === "YES").length,
              nonCarcinogen: data.filter((f) => f.carcinogen === "NO").length,
              totalAirReleases: data.reduce((sum, f) => sum + f.airReleases, 0),
              totalWaterReleases: data.reduce(
                (sum, f) => sum + f.waterReleases,
                0
              ),
              totalLandReleases: data.reduce(
                (sum, f) => sum + f.landReleases,
                0
              ),
              totalReleases: data.reduce((sum, f) => sum + f.totalReleases, 0),
              avgReleasesPerFacility: (
                data.reduce((sum, f) => sum + f.totalReleases, 0) / data.length
              ).toFixed(0),
            });

            setLoading(false);
          },
        });
      })
      .catch((error) => {
        console.error("Error loading data:", error);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    let filtered = [...facilities];

    if (filters.carcinogen !== "all") {
      filtered = filtered.filter(
        (f) => f.carcinogen === (filters.carcinogen === "yes" ? "YES" : "NO")
      );
    }

    if (filters.industry !== "all") {
      filtered = filtered.filter((f) => f.industrySector === filters.industry);
    }

    if (filters.county !== "all") {
      filtered = filtered.filter((f) => f.county === filters.county);
    }

    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(
        (f) =>
          f.facilityName?.toLowerCase().includes(searchLower) ||
          f.parentCompany?.toLowerCase().includes(searchLower) ||
          f.city?.toLowerCase().includes(searchLower) ||
          f.chemical?.toLowerCase().includes(searchLower)
      );
    }

    const filteredGrouped = Object.values(
      filtered.reduce((acc, row) => {
        const key = `${row.facilityName}-${row.latitude}-${row.longitude}`;
        if (!acc[key]) {
          acc[key] = { ...row, reports: [row] };
        } else {
          acc[key].reports.push(row);
          acc[key].airReleases += row.airReleases;
          acc[key].waterReleases += row.waterReleases;
          acc[key].landReleases += row.landReleases;
          acc[key].totalReleases += row.totalReleases;
        }
        return acc;
      }, {})
    );

    setFilteredFacilities(filtered);
    setGroupedFacilities(filteredGrouped);
  }, [filters, facilities]);

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
    setShouldResetView(false);
  };

  const handleResetFilters = () => {
    setFilters({
      carcinogen: "all",
      industry: "all",
      county: "all",
      searchTerm: "",
    });
    setShouldResetView(true);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading Chemical Release Data...</p>
      </div>
    );
  }

  return (
    <div className="App">
      <Header stats={stats} />
      <div className="main-container">
        <Sidebar
          filters={filters}
          onFilterChange={handleFilterChange}
          onResetFilters={handleResetFilters}
          facilities={facilities}
          filteredCount={filteredFacilities.length}
          onOpenReportModal={() => setIsReportModalOpen(true)}
        />
        <div className="map-container">
          <Map
            facilities={filteredFacilities}
            groupedFacilities={groupedFacilities}
            shouldResetView={shouldResetView}
          />
          <ReportModal
            isOpen={isReportModalOpen}
            onClose={() => setIsReportModalOpen(false)}
            facilities={filteredFacilities}
            filters={filters}
          />
          <Legend stats={stats} filteredCount={filteredFacilities.length} />
        </div>
      </div>
    </div>
  );
}

export default App;
